import { useRouter } from '@abyss/web/hooks/useRouter';
import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { Breadcrumbs } from '@abyss/web/ui/Breadcrumbs';
import { Heading } from '@abyss/web/ui/Heading';
import { PageBody } from '@abyss/web/ui/PageBody';
import { Router } from '@abyss/web/ui/Router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import find from 'lodash/find';
import { Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import GuestFooter from '../../common/GuestFooter/GuestFooter';
import GuestHeader from '../../common/GuestHeader/GuestHeader';
import { LoadingSpinnerComp } from '../../common/Loader';
import { getFeatureFlag } from '../../common/Utils/getFeatureFlag';
import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent';
import { useGetPlanDefinitions } from '../../hooks/useGetPlanDefinitions';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useGuestStore } from '../../store/useGuestStore';
import { GuestData, GuestState } from '../../store/useGuestStore/guestStore';
import { DateTime } from '../../utils/DateTime';
import { getIsIFPEarlyPay } from '../../utils/guest.utils';
import {
  buildPortalData,
  getAvailablePlans,
  saveSelectedPlanForSession,
} from './Guest.utils';
import { GuestPlanSelectionCoachmark } from './GuestPlanSelectionCoachmark';
import {
  CHOOSE_PCP_ROUTES,
  DEFAULT_ROUTES,
  GuestRoute,
  PLAN_SELECTOR_ROUTES,
  SEARCH_RESULTS_ROUTES,
} from './GuestRoutesConstants';
import { getBlueGreenGQLApiUrl, isLawwGuest } from './utils';

export const getGuestRoutes = (guestData: GuestData): GuestRoute[] => {
  const isIFPEarlyPay = getIsIFPEarlyPay(guestData);

  if (isIFPEarlyPay) {
    return [...DEFAULT_ROUTES, ...SEARCH_RESULTS_ROUTES, ...CHOOSE_PCP_ROUTES];
  }

  return [...PLAN_SELECTOR_ROUTES];
};

const GuestContentWrapper = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const GuestFooterWrapper = styled('div', {
  marginTop: '2rem',
  width: '100%',
});

export const GuestHeaderWrapper = ({
  backgroundColor = '$white',
  height = 'auto',
  children,
}) => (
  <Box
    color={backgroundColor}
    css={{
      'abyss-box-root': {
        '@screen < $md': {
          borderBottom: 'solid',
          borderColor: '$headerCardWrapperBorderColor',
        },
        borderBottom: 'solid 1px $gray3',
        padding: '24px',
        paddingLeft: '72px',
        height,
      },
    }}
  >
    {children}
  </Box>
);

export const GuestRoutes = () => {
  const { navigate } = useRouter();
  const { guestData, chooseType } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestData: state.guestData,
      chooseType: state.chooseType,
    }))
  );

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;

  const delsysCode = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.DELSYS_CODE
  ) as string;

  const lineOfBusiness = JSON.parse(
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.LOB) || '""'
  ) as string;

  const coverageTypeCode = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.COVERAGE_TYPE
  ) as string;

  const isGuestDeepLinkFlow =
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.SOURCE) ===
      'deeplink' &&
    window.location.pathname.includes('/guest-plan-selection/browse');

  const [isPortalStateReady, setIsPortalStateReady] = useState<boolean>(false);

  const [, getPlanDetails] = useGetPlanDefinitions({
    onCompleted: (data) => {
      const availablePlans = getAvailablePlans(data?.data.getPlanDefinitions);

      if (availablePlans.length > 1) {
        // if multiple plans returned, save available plans to session storage and navigate to plan selection page
        const availablePlansFromSession = sessionStorage.getItem(
          Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS
        );
        if (JSON.stringify(availablePlans) !== availablePlansFromSession) {
          sessionStorage.setItem(
            Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS,
            JSON.stringify(availablePlans)
          );
          window.location.href = `/guest-plan-selection/plan-selection?planSelectionLob=${lineOfBusiness}&coverageType=${coverageTypeCode}&chipValue=${chipValue}`;
          return;
        } else {
          setIsPortalStateReady(true);
          return;
        }
      } else if (availablePlans.length === 0) {
        // if no plan details found, navigate back to plan selection and remove delsysCode from session storage
        sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.DELSYS_CODE);
        window.location.href = `/guest-plan-selection/plan-selection?planSelectionLob=${lineOfBusiness}&coverageType=${coverageTypeCode}&chipValue=${chipValue}`;
        return;
      }
      const stateCode = JSON.parse(
        sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION) ??
          '{}'
      ).stateCode;
      const planDetails = availablePlans[0].planDetails[0];
      const planCategory = availablePlans[0].planCategory;
      // clear available plans to prevent stale data issues
      sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS);
      saveSelectedPlanForSession({
        selectedPlan: planDetails,
        planYear: planDetails?.years?.[0].planYear,
        coverageType: coverageTypeCode,
        lob: lineOfBusiness,
        stateCode,
        planCategory,
      });
      const signInUrl = find(lagoonUIMessage, {
        key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
      })?.url;
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.PORTAL_STATE,
        JSON.stringify(
          buildPortalData(
            planDetails,
            lineOfBusiness,
            coverageTypeCode,
            stateCode,
            planDetails?.years?.[0].planYear,
            signInUrl || '',
            planCategory
          )
        )
      );
      setIsPortalStateReady(true);
    },
    onError: (error) => {
      // Error will be handled by useCustomQuery, which will navigate to error-widget for deeplinks
      // eslint-disable-next-line no-console
      console.error('Error fetching plan definitions:', error);
    },
  });

  const routes: GuestRoute[] = getGuestRoutes(guestData);

  // Add event listener for navigation requests from nested parcels
  useEffect(() => {
    const handleGuestNavigation = (event) => {
      const { path, params } = event.detail;
      const queryString = new URLSearchParams(params).toString();
      const fullPath = `${path}${queryString ? `?${queryString}` : ''}`;
      navigate(fullPath);
    };

    window.addEventListener('guestNavigationRequest', handleGuestNavigation);

    // Cleanup event listener
    return () => {
      window.removeEventListener(
        'guestNavigationRequest',
        handleGuestNavigation
      );
    };
  }, [navigate]);
  const isLawwGuestExperience = isLawwGuest();
  const lagoonUIMessage = useLagoon('ui-messaging')();
  const featureFlags = useLagoon('feature-flags')();
  const typeOfCareText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_CARE,
  })?.message;
  const typeOfMedicalCoverageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_MEDICAL_COVERAGE,
  })?.message;
  const planSelection = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLAN_SELECTION,
  })?.message;
  const yearSelection = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION,
  })?.message;
  const planSelectionCoachmarkTitle = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLAN_SELECTION_COACHMARK_TITLE,
  })?.message;
  const planSelectionCoachmarkContent = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.PLAN_SELECTION_COACHMARK_CONTENT,
  })?.message;

  const { getSearchParams } = useRouter();
  const location = useLocation();
  const [searchParams] = getSearchParams();
  const lob = searchParams?.get('planSelectionLob');
  const coverageType = searchParams?.get('coverageType');
  const isOnPlanYearSelection = location.pathname === '/plan-year-selection';
  const url = window.location.href;
  const isFindcareLoaded = url.includes('/browse');

  const isFirstTimeUserBannerEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.GUEST_FIRST_TIME_USER_BANNERS_ENABLED
  );

  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );

  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );

  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const [saveEducationalContent, setEducationalContentStatus] =
    useSaveEducationalContentStatus({
      url: guestBGApiUrl,
    });

  // Track if user has manually dismissed the coachmark
  const [isCoachmarkDismissed, setIsCoachmarkDismissed] = useState(
    () =>
      sessionStorage.getItem('guestPlanSelectionCoachmarkDismissed') === 'true'
  );

  // Track if we've already saved that the user viewed the coachmark this session
  const [hasMarkedAsViewed, setHasMarkedAsViewed] = useState(false);

  // Track if coachmark was shown this session (prevents flicker when store updates)
  const [wasShownThisSession, setWasShownThisSession] = useState(false);

  const handleCloseCoachmark = () => {
    setIsCoachmarkDismissed(true);
    sessionStorage.setItem('guestPlanSelectionCoachmarkDismissed', 'true');
  };

  const shouldShowCoachmark =
    isFirstTimeUserBannerEnabled &&
    location.pathname.includes('plan-selection') &&
    !isCoachmarkDismissed &&
    (wasShownThisSession ||
      educationalContentStore?.hasViewedGuestReadyToEnrolCoach !== true);

  // Save to backend when coachmark is first shown (not when dismissed)
  useEffect(() => {
    if (
      shouldShowCoachmark &&
      !hasMarkedAsViewed &&
      educationalContentStore &&
      educationalContentStore.hasViewedGuestReadyToEnrolCoach === false
    ) {
      setHasMarkedAsViewed(true);
      setWasShownThisSession(true);
      setEducationalContentStatus({
        variables: {
          ...educationalContentStore,
          hasViewedContent: educationalContentStore?.hasViewedContent ?? true,
          hasViewedGuestReadyToEnrolCoach: true,
        },
      });
    }
  }, [shouldShowCoachmark, hasMarkedAsViewed, educationalContentStore]);

  // Update local store when mutation completes
  useEffect(() => {
    if (saveEducationalContent?.data?.saveEducationalContentStatus) {
      setEducationalContentStore(
        saveEducationalContent.data.saveEducationalContentStatus
      );
    }
  }, [saveEducationalContent?.data, setEducationalContentStore]);

  const isErrorPage = window.location.pathname.includes('/unable-to-load-plan');

  useEffect(() => {
    // Skip API call if on error page
    if (isErrorPage) {
      setIsPortalStateReady(true);
      return;
    }

    if (
      isGuestDeepLinkFlow &&
      lineOfBusiness &&
      coverageTypeCode &&
      delsysCode
    ) {
      getPlanDetails({
        variables: {
          planYear: String(
            DateTime.getCurrentDateTimeWithCentralOffset().getFullYear()
          ),
          lob: lineOfBusiness,
          coverageType: coverageTypeCode,
          isDualYear: true,
          isEI: lineOfBusiness === 'EI',
          planIdentifier: delsysCode,
          portalSource: 'GUEST',
        },
      });
    } else {
      setIsPortalStateReady(true);
    }
  }, [
    lineOfBusiness,
    coverageTypeCode,
    delsysCode,
    isErrorPage,
    isGuestDeepLinkFlow,
  ]);

  if (!isPortalStateReady) {
    return <LoadingSpinnerComp isLoading={true} />;
  }

  return (
    <PageBody
      css={{
        'abyss-page-body-container': {
          backgroundColor: '$pageBodyBgColor',
          maxWidth: '100%',
          position: 'relative',
          minHeight: '100vh',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      id="main-content"
      tabIndex={-1}
    >
      <GuestContentWrapper>
        {!isFindcareLoaded && !isLawwGuestExperience && <GuestHeader />}
        {lob && (
          <GuestHeaderWrapper backgroundColor={'$bgHeaderColor'}>
            <Breadcrumbs
              css={{
                'abyss-breadcrumb-current': {
                  fontWeight: 400,
                },
              }}
              items={[
                { href: '/', title: typeOfMedicalCoverageText },
                ...(!isLawwGuestExperience
                  ? [
                      {
                        href: `/select-coverage-type?planSelectionLob=${lob}`,
                        title: typeOfCareText,
                      },
                    ]
                  : []),
                ...(coverageType &&
                (!isLawwGuestExperience || !isOnPlanYearSelection)
                  ? [
                      {
                        href: `/plan-selection?planSelectionLob=${lob}&coverageType=${coverageType}`,
                        title: planSelection,
                      },
                    ]
                  : []),
                ...(isOnPlanYearSelection
                  ? [
                      {
                        href: `/plan-year-selection?planSelectionLob=${lob}&coverageType=${coverageType}`,
                        title: yearSelection,
                      },
                    ]
                  : []),
              ]}
            />
            <div style={{ position: 'relative' }}>
              <div
                id="coachmark-anchor"
                style={{ display: 'inline-block', maxWidth: '100%' }}
              >
                <Heading css={{ marginTop: '8px' }} offset={1}>
                  {chooseType}
                </Heading>
              </div>
              {shouldShowCoachmark && (
                <GuestPlanSelectionCoachmark
                  content={planSelectionCoachmarkContent || ''}
                  onClose={handleCloseCoachmark}
                  title={planSelectionCoachmarkTitle || ''}
                />
              )}
            </div>
          </GuestHeaderWrapper>
        )}

        <Suspense fallback={null}>
          <Router.Routes>
            <Router.Route>
              {routes.map((route) => (
                <Router.Route
                  element={<route.element />}
                  key={route.path}
                  path={route.path}
                />
              ))}
            </Router.Route>
          </Router.Routes>
        </Suspense>
      </GuestContentWrapper>
      {!isFindcareLoaded && (
        <GuestFooterWrapper>
          {!isLawwGuestExperience && <GuestFooter />}
        </GuestFooterWrapper>
      )}
    </PageBody>
  );
};
